import * as d3 from "d3";
import tooltip from "d3-tip";
import {
  getYAxisLabel,
  getNoDataFoundLabel,
  getChartDataSet
} from "../../../utils/graph_service";
import { groupBarChartUtility } from "./groupBarChartUtility";
import { wrapText } from "../../../utils/helper";

/** Number with commas */
function numberWithCommas(num) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function BarChartUtility(
  chartConfig,
  divId,
  onDrillDown,
  drillDownConfig,
  level,
  allowDrillDown
) {
  const margin = { top: 20, right: 20, bottom: 70, left: 40 },
    height = 300 - margin.top - margin.bottom;
    let width = 300 - margin.left - margin.right;

  const node = document.getElementById(divId);
  if (!chartConfig.data || !node) return;

  if (node !== null) {
    while (node.firstChild) {
      node.removeChild(node.firstChild);
    }
  }
  const windowWidth = node.offsetWidth;
  if (windowWidth > width) {
    width = windowWidth - margin.left - margin.right;
  }

  RenderBarChart();

  /** Create bar chart based on recieved information */
  function RenderBarChart() {
    // console.log("here", drillDownConfig.isDrillDown, drillDownConfig.key);
    if (drillDownConfig.isDrillDown && allowDrillDown == true) {
      const drillDownColumnsConfig = { ...chartConfig.configuration };
      const filters = [];
      if (chartConfig.configuration.isRepeated == true) {
        filters.push({
          key: chartConfig.configuration.autoGenerate.key,
          values: [drillDownConfig.config.find(x => x.level == level + 1).key]
        });
        drillDownColumnsConfig.autoGenerate.filters = filters;
      } else {
        drillDownColumnsConfig.columns = [
          chartConfig.configuration.columns.find(
            x =>
              x.name ==
              [drillDownConfig.config.find(x => x.level == level + 1).key]
          )
        ];
      }

      // get chart dataset
      const resData = getChartDataSet(
        chartConfig,
        drillDownConfig,
        drillDownColumnsConfig
      );
      if (drillDownConfig.currentLevel == level + 1) {
        drillDownConfig.isDrillDown = false;
      }
      // console.log("here resData", resData);
      const config = {
        startDate: chartConfig.startDate,
        endDate: chartConfig.endDate,
        configuration: drillDownColumnsConfig,
        rawData: chartConfig.rawData,
        data: resData.data,
        yAxisLabel: "count",
        type: "GroupBarChart",
        keys: resData.barKeys,
        colors: resData.barColors,
        accessor: resData.chartAccessor.mainAccessor
      };
      groupBarChartUtility(
        config,
        divId,
        onDrillDown,
        drillDownConfig,
        level + 1,
        allowDrillDown
      );
    } else {
      return generateBarChart(
        chartConfig.data,
        chartConfig.keys,
        chartConfig.colors,
        divId,
        chartConfig.yAxisLabel
      );
    }
  }

  /** Generate bar chart based on infomation or parameters given */
  function generateBarChart(data, keys, colors, divId, yAxisLabel) {
    const x = d3
      .scaleBand()
      .range([0, width])
      .padding(0.2);

    const y = d3.scaleLinear().rangeRound([height, 0]);

    const svgContainer = d3.select("#" + divId).append("svg");
    let axisFont = "0.767rem";
    if (data.length > 12) {
      axisFont = "0.467rem";
    }

    const svg = svgContainer
      .append("g")
      .attr(
        "transform",
        "translate(" + (margin.left + margin.top) + "," + margin.top + ")"
      );

    x.domain(
      data.map(function(d) {
        return d.attributeName;
      })
    );
    y.domain([
      0,
      d3.max(data, function(d) {
        return parseFloat(d.attributeValue).toFixed(2);
      })
    ]);

    svg
      .append("g")
      .attr("class", "x axis")
      .attr("transform", "translate(0," + height + ")")
      .call(d3.axisBottom(x).ticks(1))
      .selectAll("text")
      .call(wrapText, x.bandwidth())
      .style("text-anchor", "revert")
      .style("font-family", "roboto")
      .style("font-weight", "500")
      .style("font-size", axisFont)
      .attr("transform", "rotate(0)");

    svg
      .append("g")
      .attr("class", "y axis")
      .style("font-family", "roboto")
      .style("font-weight", "500")
      .style("font-size", axisFont)
      .call(d3.axisLeft(y).ticks(null));

    getYAxisLabel(yAxisLabel, svg, -55, 0 - height / 2 - 10, -90);

    document.querySelectorAll(".tooltip" + divId).forEach(function(a) {
      a.remove();
    });
    const tip = tooltip()
      .attr("class", "d3-tip tooltip" + divId)
      .html(function(d) {
        return d.attributeName + ": " + d.attributeValue.toString();
      });
    svg.call(tip);

    let totalCount = 0;
    data.forEach(function(d) {
      totalCount += parseFloat(d.attributeValue).toFixed(2);
      return d;
    });

    if (totalCount == 0) {
      getNoDataFoundLabel(
        chartConfig.startDate + " to " + chartConfig.endDate,
        svg,
        height / 2,
        (width + margin.left + margin.right) / 2
      );
    } else {
      svg
        .selectAll("bar")
        .data(data)
        .enter()
        .append("rect")
        .attr("text-anchor", "middle")
        .style("fill", function(d, i) {
          return colors[i];
        })
        .attr("x", function(d) {
          return x(d.attributeName);
        })
        .attr("width", x.bandwidth())
        .attr("y", function(d) {
          return y(parseFloat(d.attributeValue).toFixed(2));
        })
        .attr("height", function(d) {
          return height - y(parseFloat(d.attributeValue).toFixed(2));
        })
        .on("mouseover", function(d) {
          tip.show(d, this);

          d3.selectAll("rect")
            .transition()
            .duration(200)
            .style("opacity", 0.5);
          d3.select(this)
            .transition()
            .duration(200)
            .style("opacity", 1)
            .style("stroke-width", "2.5px");
        })
        .on("mouseout", function() {
          tip.hide();
          d3.selectAll("rect")
            .transition()
            .duration(200)
            .style("opacity", 1)
            .style("stroke-width", "1.5px");
        })
        .on("click", function(d) {
          //alert("jj");
          tip.hide();
          onDrillDown(
            true,
            d.attributeName,
            chartConfig.accessor,
            "",
            level + 1
          );
        });
    }

    const svgSize = svg.node().getBBox();
    let legendLine = svgSize.height + 20;
    let currentX = margin.left;
    const legend = svgContainer
      .append("g")
      .attr("class", "legend-container")
      .attr("width", width);

    legend
      .selectAll("g")
      .data(data)
      .enter()
      .append("g")
      .each(function(d, i) {
        const g = d3.select(this);
        let y = legendLine;
        const rect = g
          .append("rect")
          .attr("y", y)
          .attr("x", currentX)
          .attr("width", 10)
          .attr("height", 10)
          .attr(
            "class",
            d.attributeName.replace(/[^a-zA-Z0-9]/g, "") + " legend"
          )
          .style("fill", colors[i]);

        const size = rect.node().getBBox();
        currentX += size.width + 10;
        if (currentX > width) {
          currentX = margin.left;
          legendLine += 20;
          y = legendLine;
          rect.attr("x", currentX).attr("y", y);
          currentX += size.width + 10;
        }
        const recText = g
          .append("text")
          .attr("y", y + 10)
          .attr("x", currentX)
          .attr("height", 30)
          .attr("width", 170)
          .attr(
            "class",
            d.attributeName.replace(/[^a-zA-Z0-9]/g, "") + " legend"
          )
          .style("fill", colors[i])
          .style("font-family", "roboto")
          .style("font-weight", "500")
          .style("font-size", "12px")
          .text(function(d) {
            return (
              d.attributeName + " (" + numberWithCommas(d.attributeValue) + ")"
            );
          });
        const recTextSize = recText.node().getBBox();
        currentX += recTextSize.width + 20;
        if (currentX > width) {
          currentX = margin.left;
          legendLine += 20;
          y = legendLine;
          rect.attr("x", currentX).attr("y", y);
          currentX += size.width + 10;
          recText.attr("x", currentX).attr("y", y + 10);
          currentX += recTextSize.width + 20;
        }
      });

    const legendSize = legend.node().getBBox();
    svgContainer.attr("height", svgSize.height + legendSize.height + 20);
    svgContainer.attr("width", svgSize.width + 10);
    //.attr("viewBox", [0, 0, width+margin.left+margin.right, height + margin.top + margin.bottom]);
  }
}

export { BarChartUtility };
